/**
 * Country-keyed defaults for an event's currency and timezone.
 *
 * Both are defaults an organiser can override, not derived truth. Currency is a
 * straight ISO 3166 -> ISO 4217 mapping. Timezone is deliberately absent for any
 * country spanning more than one zone — guessing "the first US zone" would be
 * wrong for most US events, so the caller falls back to the browser's own zone.
 *
 * Covers the countries the app's events actually appear in; an unmapped country
 * returns an empty string, leaving the field for the organiser to fill.
 */
const CURRENCY_BY_COUNTRY: Record<string, string> = {
    GB: 'GBP',
    IE: 'EUR',
    US: 'USD',
    CA: 'CAD',
    AU: 'AUD',
    NZ: 'NZD',
    DE: 'EUR',
    FR: 'EUR',
    ES: 'EUR',
    IT: 'EUR',
    NL: 'EUR',
    BE: 'EUR',
    AT: 'EUR',
    PT: 'EUR',
    FI: 'EUR',
    GR: 'EUR',
    PL: 'PLN',
    CZ: 'CZK',
    DK: 'DKK',
    SE: 'SEK',
    NO: 'NOK',
    CH: 'CHF',
    JP: 'JPY',
    SG: 'SGD',
    ZA: 'ZAR'
};

/** Only countries that sit in exactly one zone. */
const TIME_ZONE_BY_COUNTRY: Record<string, string> = {
    GB: 'Europe/London',
    IE: 'Europe/Dublin',
    NZ: 'Pacific/Auckland',
    DE: 'Europe/Berlin',
    FR: 'Europe/Paris',
    ES: 'Europe/Madrid',
    IT: 'Europe/Rome',
    NL: 'Europe/Amsterdam',
    BE: 'Europe/Brussels',
    AT: 'Europe/Vienna',
    PT: 'Europe/Lisbon',
    FI: 'Europe/Helsinki',
    GR: 'Europe/Athens',
    PL: 'Europe/Warsaw',
    CZ: 'Europe/Prague',
    DK: 'Europe/Copenhagen',
    SE: 'Europe/Stockholm',
    NO: 'Europe/Oslo',
    CH: 'Europe/Zurich',
    JP: 'Asia/Tokyo',
    SG: 'Asia/Singapore',
    ZA: 'Africa/Johannesburg'
};

const lookup = (table: Record<string, string>, code: string | undefined | null): string =>
    code ? (table[code.toUpperCase()] ?? '') : '';

/** ISO 4217 currency for a country, or '' when there is no confident answer. */
export const currencyForCountry = (code: string | undefined | null): string => lookup(CURRENCY_BY_COUNTRY, code);

/** IANA timezone for a single-timezone country, or '' when the country spans several. */
export const timeZoneForCountry = (code: string | undefined | null): string => lookup(TIME_ZONE_BY_COUNTRY, code);
